type DescriptionItem = {
  name: string;
  description: string;
};

type Item = {
  name: string;
  description: string;
};

type PriceOption = {
  value: number;
  label: string;
};

type DurationPlan = {
  type: string;
  currency: string;
  period: number;
  price: number;
  actuall_price: number;
  desc: string;
  discount: number;
};

type DurationMap = {
  [key: string]: DurationPlan;
};

export function parseDoubleColonDescriptions(htmlDescriptions: string): DescriptionItem[] {
  // Regex to match lines that start with "Name::"
  const regex = /([A-Za-z *]+?)::\s*/g;

  // Insert a unique delimiter before each matched key
  const prepared = htmlDescriptions.replace(regex, '||$1::');

  // Split the string into lines and filter out any empty entries
  const lines = prepared.split('||').filter((line: string) => line.trim() !== '');

  // Convert lines into structured objects
  return lines.map((line: string): DescriptionItem => {
    const [name, ...rest] = line.split('::');
    return {
      name: name.trim(),
      description: rest.join('::').trim()
    };
  });
}


export function getDescriptionByName(array :Item[], keyName:string) {
  const match = array.find((item: Item) => item.name === keyName);
  return match ? match.description : "NA";
}

export function replaceMultiple(str: string, replacements: Record<string, string>): string {
  for (const [key, value] of Object.entries(replacements)) {
    str = str.split(key).join(value);
  }
  return str;
}

export function formatDurations(durationObj :DurationMap) {
  const result = [];

  for (const key in durationObj) {
    const item = durationObj[key];
    const months = item.period;
    const monthlyPrice = parseFloat((item.actuall_price / months).toFixed(2));

    let periodLabel;
    if (months >= 12) {
      const years = months / 12;
      periodLabel = `${years} ${years === 1 ? 'year' : 'years'}`;
    } else {
      periodLabel = `${months} ${months === 1 ? 'month' : 'months'}`;
    }
    const itemData : PriceOption = {
      value: monthlyPrice,
      label: `${monthlyPrice} per month (${periodLabel})`
    } ;
    result.push(itemData);
  }

  return result;
}

export function parseFeatureList<
  T extends readonly string[]
>(
  description: string,
  keywords: T,
  keywordMap: Record<T[number], string>
) {
  return description
    .split(/\r?\n/)
    .filter(line => line.includes(':'))
    .map(line => {
      const [key, ...rest] = line.split(':');
      return key.trim() + ' - ' + rest.join(':').trim();
    })
    .filter(line =>
      keywords.some(keyword =>
        line.toLowerCase().includes(keyword.toLowerCase())
      )
    )
    .map(line => {
      for (const [keyword, label] of Object.entries(keywordMap)) {
        if (line.toLowerCase().includes(keyword.toLowerCase())) {
          const [, ...value] = line.split(/[-:]/);
          if(label == ""){
            return `${value.join('-').trim()}`
          }
          return `${label}: ${value.join(':').trim()}`;
        }
      }
      return null;
    })
    .filter(Boolean);
}

export function parseManagedFeaturesList(description: string, slug: string = "") {
  type KeywordMap = Record<string, string>;

  const oracleKeywords = ["Workspace/Schema", "Oracle Tablespace Quota", "version", "OS users/SFTP users", "Per day request quota", "PDF Reporting"];
  const oracleKeywordMap: KeywordMap = {
    "Workspace/Schema": "Workspace/Schema",
    "Oracle Tablespace Quota": "Oracle Tablespace Quota",
    "version": "version",
    "OS users/SFTP users": "OS users/SFTP users",
    "Per day request quota": "Per day request quota",
    "PDF Reporting": "PDF Reporting"
  };

  const defaultKeywords = ["Memory", "CPU", "Cloud Storage", "Premium Bandwidth"];
  const defaultKeywordMap: KeywordMap = {
    "Memory": "RAM",
    "CPU": "CPU",
    "Cloud Storage": "Storage",
    "Premium Bandwidth": "Bandwidth"
  };

  const isOracle = slug === "managed-oracle-hosting";
  const keywords = isOracle ? oracleKeywords : defaultKeywords;
  const keywordMap = isOracle ? oracleKeywordMap : defaultKeywordMap;

  return parseFeatureList(description, keywords, keywordMap);
}

export function parseServerFeaturesList(description : string){
  const keywords = ["No. of CPU Cores", "RAM", "Hard Drive(s)", "FBandwidth (Monthly)"] as const;
  const keywordMap: Record<typeof keywords[number], string> = {
    "No. of CPU Cores": "CPU",
    "RAM": "RAM",
    "Hard Drive(s)": "Storage",
    "FBandwidth (Monthly)": "Bandwidth",
  };

  return parseFeatureList(description, keywords, keywordMap);
}

export function parseServerMacFeaturesList(description : string){
  const keywords = ["Processor", "CPU Frequency", "No. of CPU Cores", "No. of GPU Cores", "RAM", "Hard Drive(s)", "FBandwidth (Monthly)"] as const;
  const keywordMap: Record<typeof keywords[number], string> = {
    "Processor" : "Processor",
    "CPU Frequency" : "CPU Frequency",
    "No. of CPU Cores": "CPU Cores",
    "No. of GPU Cores" : "GPU Cores",
    "RAM": "RAM",
    "Hard Drive(s)": "Storage",
    "FBandwidth (Monthly)": "Bandwidth",
  };

  return parseFeatureList(description, keywords, keywordMap);
}

export function parseWRPFeaturesList(description : string){
  const keywords = ["Memory", "CPU", "Raid Protected Storage", "Premium Bandwidth"] as const;
  const keywordMap: Record<typeof keywords[number], string> = {
    "Memory": "RAM",
    "CPU": "CPU",
    "Raid Protected Storage": "Storage",
    "Premium Bandwidth": "Bandwidth",
  };

  return parseFeatureList(description, keywords, keywordMap);
}

export function parseHostingSeoFeaturesList(description : string){
  const keywords = ["Raid Protected Storage", "Bandwidth"] as const;
  const keywordMap: Record<typeof keywords[number], string> = {
    "Raid Protected Storage": "Storage",
    "Bandwidth": "Bandwidth",
  };

  return parseFeatureList(description, keywords, keywordMap);
}

export function parseName(description : string, key: string){
  const keywords = [key] as const;
  const keywordMap: Record<typeof keywords[number], string> = {
    [key] : ""
  };

  return parseFeatureList(description, keywords, keywordMap);
}

export function getCountryName(slug:string){
  if(slug == "usa"){
    return "USA";
  }else if(slug == "uae"){
    return "UAE";
  }else if(slug == "unitedkingdom"){
    return "United Kingdom";
  }else{
    return slug;
  }
}